import {useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Alert, Button, Input, Table} from 'antd';
import {GET_COMPLY_CUBE_LIVE_PHOTOS} from '../resolvers';
import {
  buildLivePhotosInput,
  formatBytes,
  formatDate,
  unwrapListResponse,
  COMPLY_CUBE_LIVE_PHOTOS_KEY,
} from '../kycComplyCubeUtils';
import {
  ComplyCubeTablePagination,
  ComplyCubeTruncatedClientId,
  ComplyCubeUserCell,
  firstHttpUrl,
  LivePhotoThumb,
} from './ComplyCubeTableShared';
import styles from '../kycDashboard.module.scss';

export function ComplyCubeLivePhotosTab({openClientDetail}) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchDraft, setSearchDraft] = useState('');
  const [clientIdDraft, setClientIdDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedClientId, setAppliedClientId] = useState('');

  const showReset =
    searchDraft.trim() !== '' ||
    clientIdDraft.trim() !== '' ||
    appliedSearch.trim() !== '' ||
    appliedClientId.trim() !== '';

  const handleApply = () => {
    setAppliedSearch(searchDraft);
    setAppliedClientId(clientIdDraft.trim());
    setPage(1);
  };

  const handleReset = () => {
    setSearchDraft('');
    setClientIdDraft('');
    setAppliedSearch('');
    setAppliedClientId('');
    setPage(1);
  };

  const variables = useMemo(
    () => ({
      input: buildLivePhotosInput(page, limit, {
        search: appliedSearch,
        clientId: appliedClientId,
      }),
    }),
    [page, limit, appliedSearch, appliedClientId],
  );

  const {data, loading} = useQuery(GET_COMPLY_CUBE_LIVE_PHOTOS, {
    variables,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const {error, rows, total} = unwrapListResponse(
    data,
    COMPLY_CUBE_LIVE_PHOTOS_KEY,
    'ResponseWithComplyCubeLivePhotos',
  );

  return (
    <>
      {error ? (
        <Alert
          type="error"
          message={error.message || 'Request failed'}
          showIcon
        />
      ) : null}
      <div className={styles.viewerFilters}>
        <Input
          allowClear
          placeholder="Search…"
          className={styles.viewerSearch}
          value={searchDraft}
          onChange={e => setSearchDraft(e.target.value)}
          onPressEnter={handleApply}
        />
        <Input
          allowClear
          placeholder="Client ID"
          className={styles.viewerFilterInput}
          value={clientIdDraft}
          onChange={e => setClientIdDraft(e.target.value)}
          onPressEnter={handleApply}
        />
        <div className={styles.viewerFilterActions}>
          <Button type="primary" onClick={handleApply}>
            Submit
          </Button>
          {showReset ? <Button onClick={handleReset}>Reset</Button> : null}
        </div>
      </div>
      <Table
        className="antd-table"
        loading={loading}
        pagination={false}
        rowKey={r => r.id || r._id}
        dataSource={rows}
        onRow={record => ({
          onClick: () => {
            const cid = record?.clientId;
            if (cid) openClientDetail(cid);
          },
          className: styles.clickableRow,
        })}
        columns={[
          {
            title: 'User',
            key: 'user',
            width: 228,
            render: (_, r) => <ComplyCubeUserCell user={r?.user} />,
          },
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
          },
          {
            title: 'Client ID',
            dataIndex: 'clientId',
            key: 'clientId',
            width: 168,
            render: id => <ComplyCubeTruncatedClientId id={id} />,
          },
          {
            title: 'Preview',
            key: 'preview',
            render: (_, r) => (
              <LivePhotoThumb src={firstHttpUrl(r?.s3, r?.downloadLink)} />
            ),
          },
          {
            title: 'Liveness check',
            key: 'liveness',
            render: (_, r) =>
              r?.performLivenessCheck ? (
                <span className={styles.checkMark}>✓</span>
              ) : (
                <span className={styles.crossMark}>✗</span>
              ),
          },
          {
            title: 'Content type',
            dataIndex: 'contentType',
            key: 'contentType',
          },
          {
            title: 'Size',
            dataIndex: 'size',
            key: 'size',
            render: formatBytes,
          },
          {
            title: 'Created at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: formatDate,
          },
        ]}
      />
      <ComplyCubeTablePagination
        page={page}
        limit={limit}
        total={total}
        rowCount={rows.length}
        loading={loading}
        onPageChange={(nextPage, nextLimit) => {
          setLimit(nextLimit);
          setPage(nextPage);
        }}
        itemName="live photos"
      />
    </>
  );
}
